import {useEffect, useRef, useState} from "react";
import axios from "axios";
import {GUID} from "@utils/AppConstants";
import {OrderRecipeLineageModel} from "@pages/orders/order.models";
import {ApiUrl_OrderRecipe_GetLineage} from "@utils/Endpoints";
import {ApiResponseDto} from "@utils/Models";
import {useFlintNotificationContext} from "@components/page_components/common/notifications/FlintNotificationContext";

export interface OrderRecipeLineagePanelProps {
    AssignmentGuid?: GUID | null;
}

type LineageStatus = "idle" | "loading" | "loaded" | "error";

export function useOrderRecipeLineagePanel(props: OrderRecipeLineagePanelProps) {
    const {showNotification} = useFlintNotificationContext();
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [status, setStatus] = useState<LineageStatus>("idle");
    const [lineage, setLineage] = useState<OrderRecipeLineageModel | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        return () => {
            abortRef.current?.abort();
        };
    }, []);

    async function loadLineage() {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setStatus("loading");
        try {
            const response = await axios.get<ApiResponseDto<OrderRecipeLineageModel>>(ApiUrl_OrderRecipe_GetLineage, {
                params: {assignmentGuid: props.AssignmentGuid},
                signal: controller.signal,
            });
            setLineage(response.data.Result);
            setStatus("loaded");
        } catch (err: any) {
            if (!axios.isCancel(err)) {
                showNotification(err);
                setStatus("error");
            }
        }
    }

    function handleToggleExpand() {
        if (isExpanded) {
            setIsExpanded(false);
            return;
        }
        setIsExpanded(true);
        if (status === "idle" || status === "error") {
            void loadLineage();
        }
    }

    return {
        state: {
            isExpanded: {get: isExpanded},
            status: {get: status},
            lineage: {get: lineage},
        },
        handlers: {
            handleToggleExpand,
        },
        utils: {
            hasAssignment: !!props.AssignmentGuid,
        },
    };
}
